'use client';

import type React from 'react';
import { useCallback } from 'react';
import { toHotpadsClickstreamListing } from '@/src/analytics/hotpads/track';
import {
  type ListingActionAnalyticsContext,
  useOptionalListingActionContext,
} from '@/src/components/features/listing-actions';
import { emitTrackPhoneLeadEvent } from '@/src/components/features/property/lib/analytics';
import { useCurrentUser } from '@/src/lib/hooks/query';
import { useRecentlyContactedListing } from '@/src/lib/hooks/query/useRecentlyContactedListing';
import type { SummaryListingDetails } from '@/src/types/summaryListingDetails.type';

type ContactTriggerLocation = 'search_results' | 'user_item_list_page';

function getContactPhone(listing: SummaryListingDetails): string | null {
  return listing.contactPhone ?? listing.listedBy?.contactPhone ?? listing.companyPhoneNumber ?? null;
}

/** Props for `ListingCardContactActions` other than the message action, which the caller's contact form owns. */
export function useListingCardContactLead(listing: SummaryListingDetails, triggerLocation: ContactTriggerLocation) {
  const { data: currentUser } = useCurrentUser();
  const recentlyContacted = useRecentlyContactedListing(listing, {
    loggedIn: currentUser?.loggedIn === true,
    email: currentUser?.data?.email,
  });
  const phoneLead = useListingCardPhoneLead(listing, triggerLocation);

  return { ...phoneLead, recentlyContacted };
}

/** The phone half of the contact lead, which needs no user or contact-history subscription. */
export function useListingCardPhoneLead(listing: SummaryListingDetails, triggerLocation: ContactTriggerLocation) {
  const onCall = useCallback(() => {
    emitTrackPhoneLeadEvent({
      listing: toHotpadsClickstreamListing(listing),
      triggerLocation,
      triggerObject: 'property_card',
    });
  }, [listing, triggerLocation]);

  return { contactPhone: getContactPhone(listing), onCall };
}

export function getContactTriggerLocation(analyticsContext: ListingActionAnalyticsContext): ContactTriggerLocation {
  return analyticsContext.triggerLocation === 'user_item_list_page' ? 'user_item_list_page' : 'search_results';
}

/** Props for `ListingActionMenu`; each handler calls `stopCardNavigation` first so the card does not open. */
export function useListingCardMenuActions(
  listing: SummaryListingDetails,
  analyticsContext: ListingActionAnalyticsContext,
  stopCardNavigation: (event: React.MouseEvent) => void,
) {
  const listingActionContext = useOptionalListingActionContext();

  const onHide = useCallback(
    (event: React.MouseEvent) => {
      stopCardNavigation(event);
      listingActionContext?.toggleHideListing(listing, analyticsContext);
    },
    [analyticsContext, listing, listingActionContext, stopCardNavigation],
  );

  const onReport = useCallback(
    (event: React.MouseEvent) => {
      stopCardNavigation(event);
      void listingActionContext?.openReport(listing, analyticsContext);
    },
    [analyticsContext, listing, listingActionContext, stopCardNavigation],
  );

  return { listingHidden: listingActionContext?.isListingHidden(listing) ?? false, onHide, onReport };
}
